import { computed, ref } from 'vue'
import {
  loginSeller,
  logoutSeller,
  fetchSellerSession,
  updateSellerProfile,
  type SellerProfilePayload,
} from '@/api/seller'
import {
  clearSellerSession,
  loadSellerSession,
  saveSellerSession,
  isSellerApproved,
  type SellerSession,
} from '@/constants/sellerSession'
import { saveAutoLogin } from '@/constants/autoLogin'

const seller = ref<SellerSession | null>(loadSellerSession())
const initialized = ref(false)
const initializing = ref(false)
/** 라우터와 헤더가 동시에 물으면 한쪽은 빈 계정으로 끝내지 않도록 같은 요청을 같이 기다린다. */
let pendingInit: Promise<void> | null = null

export function useSellerAuth() {
  const isSellerLoggedIn = computed(() => Boolean(seller.value))
  const isApproved = computed(() => isSellerApproved(seller.value))
  const isPending = computed(() => seller.value?.status === 'PENDING')
  const isViewingAsAdmin = computed(() => Boolean(seller.value?.viewingAsAdmin))

  /** 수수료·배송비·정산계좌는 여러 화면이 같이 쓴다. 세션이 오기 전에도 화면이 깨지지 않도록 기본값을 둔다. */
  const policy = computed(() => ({
    feeRate: seller.value?.feeRate ?? 8,
    shippingFee: seller.value?.shippingFee ?? 3000,
    freeShipOver: 0,
    settleBank: seller.value?.settleBank || '',
    settleAccount: seller.value?.settleAccount || '',
  }))

  const setSeller = (next: SellerSession | null) => {
    seller.value = next
    if (next) {
      saveSellerSession(next)
      return
    }
    clearSellerSession()
  }

  const initSellerAuth = () => {
    if (initialized.value) {
      return Promise.resolve()
    }
    if (pendingInit) {
      return pendingInit
    }

    initializing.value = true
    pendingInit = (async () => {
      try {
        setSeller(await fetchSellerSession())
      } catch {
        /* 세션이 끝났으면 자동 로그인 표시도 같이 걷어낸다 */
        saveAutoLogin('seller', false)
        setSeller(null)
      } finally {
        initialized.value = true
        initializing.value = false
        pendingInit = null
      }
    })()

    return pendingInit
  }

  const login = async (sellerId: string, password: string, rememberMe = false) => {
    const result = await loginSeller({ sellerId, password, rememberMe })
    saveAutoLogin('seller', rememberMe)
    setSeller(result)
    initialized.value = true
    return result
  }

  const logout = async () => {
    try {
      await logoutSeller()
    } finally {
      saveAutoLogin('seller', false)
      setSeller(null)
      initialized.value = true
    }
  }

  const saveProfile = async (payload: SellerProfilePayload) => {
    const result = await updateSellerProfile(payload)
    setSeller(result)
    return result
  }

  const applySession = (next: SellerSession) => {
    setSeller(next)
    initialized.value = true
  }

  return {
    seller,
    isSellerLoggedIn,
    isApproved,
    isPending,
    isViewingAsAdmin,
    policy,
    initialized,
    initSellerAuth,
    login,
    logout,
    saveProfile,
    setSeller,
    applySession,
  }
}
